import { Injectable } from '@angular/core';

export interface CookieOptions {
  expires?: number; // Days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  /**
   * Gets a cookie by name
   * @param name The cookie name
   * @returns The cookie value or null if not found
   */
  getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const matches = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
    );

    return matches ? decodeURIComponent(matches[1]) : null;
  }

  /**
   * Sets a cookie with the specified name and value
   * @param name The cookie name
   * @param value The cookie value
   * @param options Cookie options like expiration, path, etc.
   */
  setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof document === 'undefined') {
      return;
    }

    const optionsCopy: CookieOptions & { [key: string]: any } = { ...options };

    if (options.expires) {
      const date = new Date();
      date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
      (optionsCopy as any).expires = date.toUTCString();
    }

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    for (const [key, val] of Object.entries(optionsCopy)) {
      cookieString += `; ${key}`;
      if (val !== true) {
        cookieString += `=${val}`;
      }
    }

    document.cookie = cookieString;
  }

  /**
   * Deletes a cookie by name
   * @param name The cookie name
   * @param options Cookie options like path, domain, etc.
   */
  deleteCookie(name: string, options: Omit<CookieOptions, 'expires'> = {}): void {
    this.setCookie(name, '', {
      ...options,
      expires: -1,
    });
  }
}
